import { useState } from 'react';
import { Zap, Lock } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { Sidebar } from '@/common/components/sidebar/Sidebar';
import { Dialog, DialogContent } from '@/common/components/ui/dialog';
import { BillingAddressDialogContent } from '@/features/billing-addresses/components/billing-address-dialog/BillingAddressDialog';
import { Markdown } from '@/common/components/markdown/Markdown';
import { ProductActivationForm } from '../product-activation-form/ProductActivationForm';
import { useProductPanel } from './useProductPanel';
import { SubscriptionDetails } from './SubscriptionDetails';
import { PanelFooter } from './PanelFooter';
import { RequirementsSection } from './RequirementsSection';

interface ProductDetailPanelContentProps {
  variantId: number;
  supplyPointId: number;
  canManageProducts: boolean;
}

export function ProductDetailPanelContent({
  variantId,
  supplyPointId,
  canManageProducts,
}: ProductDetailPanelContentProps) {
  const {
    variant,
    variantsForProductType,
    activeProduct,
    dependencies,
    billingAddresses,
    orgs,
    invalidateBillingAddresses,
    isActive,
    onClose,
    activateMutation,
    cancelMutation,
  } = useProductPanel(variantId, supplyPointId, canManageProducts);

  const [addAddressOpen, setAddAddressOpen] = useState(false);

  if (!variant) return null;

  const hasUnmetDependencies = dependencies.some((d) => !d.isActive);
  const description = variant.productType.description;

  return (
    <>
      <Sidebar.Header className="px-4 sm:px-6 pt-6 pb-5 border-b border-gray-100 shrink-0">
        <div className="flex size-12 items-center justify-center rounded-xl mb-4 bg-blue-50">
          <Zap size={22} className="text-blue-600" />
        </div>
        <Sidebar.Title className="text-lg font-bold text-gray-900">
          {variant.productType.name}
        </Sidebar.Title>
      </Sidebar.Header>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">
        {isActive && activeProduct && (
          <SubscriptionDetails
            product={activeProduct}
            variant={variant}
            billingAddresses={billingAddresses}
          />
        )}

        {description && <Markdown content={description} />}

        <RequirementsSection dependencies={dependencies} />

        {!isActive && !hasUnmetDependencies && canManageProducts && (
          <ProductActivationForm
            variants={variantsForProductType}
            billingAddresses={billingAddresses}
            onActivate={async (input) => {
              await activateMutation.mutateAsync(input);
            }}
            onCancel={() => onClose?.()}
            onAddBillingAddress={() => setAddAddressOpen(true)}
          />
        )}

        {!isActive && !hasUnmetDependencies && !canManageProducts && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <Lock size={11} className="shrink-0" />
            <Trans>You don&apos;t have permission to activate this product</Trans>
          </p>
        )}

        {!isActive && hasUnmetDependencies && (
          <p className="text-sm text-amber-600 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <Trans>Activate the required products above before enabling this product.</Trans>
          </p>
        )}
      </div>

      <PanelFooter
        isActive={isActive}
        productName={variant.productType.name}
        onClose={onClose}
        onCancel={canManageProducts ? () => cancelMutation.mutate() : undefined}
      />

      {/* The sidebar renders outside ModalProvider, so the dialog is mounted here directly. */}
      <Dialog open={addAddressOpen} onOpenChange={setAddAddressOpen}>
        <DialogContent className="max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <BillingAddressDialogContent
            orgs={orgs}
            onSuccess={async () => {
              setAddAddressOpen(false);
              await invalidateBillingAddresses();
            }}
            onClose={() => setAddAddressOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
